import { useDocuments } from "@/hooks/useDocuments";
import useHeaderChange from "@/hooks/useHeaderChange";
import { useParams } from "react-router";
import DocumentsSkeleton from "./DocumentsSkeleton";
import { DataGrid, Tooltip } from "devextreme-react";
import { Column } from "devextreme-react/data-grid";
import { cellRenderer, headerRenderer } from "../gifting/grid-utils";
import { formatDate, formatNumber } from "devextreme/localization";
import { LegalDocument } from "@/types/types";
import NoDataMessage from "@/components/NoDataMessage";
import withDisclaimer from "@/hoc/withDisclaimer";
import { cn } from "@/helper/helper";

function Documents() {
  const { id } = useParams();
  useHeaderChange(id!, "Estate Planning Documents");
  const { data, isLoading, isError, isRefetching } = useDocuments(id!);

  if (isLoading || isRefetching) return <DocumentsSkeleton />;
  if (isError) return <div>Failed fetching Legal Documents</div>;

  if (!data?.data.length) {
    return <NoDataMessage />;
  }

  return (
    <DataGrid
      dataSource={data?.data}
      className="ao-grid docs-horizontal print-zoom-85 grid-sticky-headers text-sm"
      showBorders={true}
      showRowLines={true}
      showColumnLines={true}
      remoteOperations={false}
      wordWrapEnabled={true}
      keyExpr={"externalCode"}
      onCellPrepared={(e) => {
        if (
          e.rowType === "header" ||
          e.column.dataField === "document" ||
          e.column.dataField === "revised" ||
          e.column.dataField === "type-provisions" ||
          e.column.dataField === "title"
        ) {
          e.cellElement.style.backgroundColor = "var(--primary-color)";
          e.cellElement.style.color = "white";
        }
      }}
    >
      <Column
        dataField="document"
        allowSorting={false}
        allowEditing={false}
        width={180}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`document`, "Document")}
        cellRender={(cellData) => {
          const doc: LegalDocument = cellData.data;
          return cellRenderer(
            cellData,
            `document-${doc?.externalCode}`,
            doc?.document,
            "",
            "h-full flex items-center min-h-[65.5px] justify-center !text-sm"
          );
        }}
      />
      <Column
        dataField="revised"
        allowSorting={false}
        allowEditing={false}
        width={150}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`revised`, "revised")}
        cellRender={(cellData) => {
          const doc: LegalDocument = cellData.data;
          return cellRenderer(
            cellData,
            `revised-${doc?.externalCode}`,
            doc?.revised ? formatDate(new Date(doc?.revised), "MM/dd/yyyy") : "",
            "",
            "h-full flex items-center min-h-[65.5px] justify-center !text-sm"
          );
        }}
      />

      <Column
        dataField="type-provisions"
        allowSorting={false}
        allowEditing={false}
        width={180}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`type-provisions`, "Type / Provisions")}
        cellRender={(cellData) => {
          const doc: LegalDocument = cellData.data;

          return doc.provisions?.length ? (
            <div className="flex h-full flex-col divide-y divide-neutral-400">
              {doc.provisions.map(({ externalCode, pages, typeProvision }) => {
                return (
                  <div
                    id={`type-provisions-${externalCode}`}
                    key={`type-provisions-${externalCode}`}
                    className={`relative flex h-full items-center justify-center py-[0.7344rem] print:px-1 print:py-1 print:!text-[10px]`}
                  >
                    {typeProvision}
                    {pages ? (
                      <img
                        className="absolute bottom-0 right-0 top-0 print:h-2 print:w-2"
                        src="/icons/danger-cell.svg"
                      />
                    ) : null}
                    {pages ? (
                      <Tooltip
                        target={`#type-provisions-${externalCode}`}
                        showEvent="mouseenter"
                        hideEvent="mouseleave"
                        hideOnOutsideClick={false}
                      >
                        <div className="h-fit max-w-[130px] whitespace-pre-line break-words">
                          {pages}
                        </div>
                      </Tooltip>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            ""
          );
        }}
      />
      <Column
        dataField="title"
        allowSorting={false}
        allowEditing={false}
        width={180}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`title`, "Title")}
        cellRender={(cellData) => {
          const doc: LegalDocument = cellData.data;
          return cellRenderer(
            cellData,
            `title-${doc?.externalCode}`,
            doc?.title,
            "",
            "h-full flex items-center min-h-[65.5px] justify-center !text-sm"
          );
        }}
      />
      <Column
        dataField="name"
        allowSorting={false}
        allowEditing={false}
        minWidth={130}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`name`, "Name")}
        cellRender={(cellData) => {
          const doc: LegalDocument = cellData.data;

          return doc?.roles?.length ? (
            <div className="flex h-full flex-col divide-y divide-neutral-400">
              {doc?.roles?.map(({ externalCode, contactName, status }) => (
                <div
                  id={`name-${externalCode}`}
                  key={`name-${externalCode}`}
                  className={`relative flex h-full items-center justify-center py-[0.7344rem] print:px-1 print:py-1 print:!text-[10px] ${status === "Deceased" ? "!bg-neutral-400" : ""}`}
                >
                  {contactName ?? ""}
                </div>
              ))}
            </div>
          ) : (
            ""
          );
        }}
      />
      <Column
        dataField="role"
        allowSorting={false}
        allowEditing={false}
        minWidth={130}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`role`, "role")}
        cellRender={(cellData) => {
          const doc: LegalDocument = cellData.data;

          return doc?.roles?.length ? (
            <div className="flex h-full flex-col divide-y divide-neutral-400">
              {doc?.roles?.map((item) =>
                cellRenderer(
                  cellData,
                  `role-${item.externalCode}`,
                  item.role,
                  "",
                  `h-full flex items-center min-h-[65.5px] justify-center px-2 !text-sm ${item.status === "Deceased" ? "!bg-neutral-400" : ""}`
                )
              )}
            </div>
          ) : (
            ""
          );
        }}
      />
      <Column
        dataField="beneficiary"
        allowSorting={false}
        allowEditing={false}
        minWidth={130}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`beneficiary`, "beneficiary")}
        cellRender={(cellData) => {
          const doc: LegalDocument = cellData.data;

          return doc?.beneficiaries?.length ? (
            <div className="flex h-full flex-col divide-y divide-neutral-400">
              {doc?.beneficiaries?.map(
                ({ externalCode, displayName, pages, perStirpes, status }) => {
                  return (
                    <div
                      id={`beneficiary-${externalCode}`}
                      key={`beneficiary-${externalCode}`}
                      className={`relative flex h-full min-h-[65.5px] items-center justify-center py-[0.7344rem] print:px-1 print:py-1 print:!text-[10px] ${status === "Deceased" ? "!bg-neutral-400" : ""}`}
                    >
                      {displayName}
                      {pages ? (
                        <>
                          <img
                            className="absolute bottom-0 right-0 top-0 print:h-2 print:w-2"
                            src="/icons/danger-cell.svg"
                          />
                          <Tooltip
                            target={`#beneficiary-${externalCode}`}
                            showEvent="mouseenter"
                            hideEvent="mouseleave"
                            hideOnOutsideClick={false}
                          >
                            <div className="h-fit max-w-[130px] whitespace-pre-line break-words">
                              {pages}
                            </div>
                          </Tooltip>
                        </>
                      ) : null}
                      {perStirpes ? (
                        <>
                          <img
                            className="relative bottom-0 right-0 top-0 my-auto print:h-2 print:w-2"
                            src="/icons/star-icon.svg"
                          />
                          <Tooltip
                            target={`#beneficiary-${externalCode}`}
                            showEvent="mouseenter"
                            hideEvent="mouseleave"
                            hideOnOutsideClick={false}
                            position={"top"}
                          >
                            <div className="h-fit max-w-[130px] whitespace-pre-line break-words">
                              Per Stirpes
                            </div>
                          </Tooltip>
                        </>
                      ) : null}
                    </div>
                  );
                }
              )}
            </div>
          ) : (
            ""
          );
        }}
      />
      <Column
        dataField="type"
        allowSorting={false}
        allowEditing={false}
        minWidth={130}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`type`, "type")}
        cellRender={(cellData) => {
          const doc: LegalDocument = cellData.data;

          return doc?.beneficiaries?.length ? (
            <div className="flex h-full flex-col divide-y divide-neutral-400">
              {doc?.beneficiaries?.map((item) =>
                cellRenderer(
                  cellData,
                  `type-${item.externalCode}`,
                  item.type,
                  "",
                  `h-full flex items-center min-h-[65.5px] justify-center !text-sm ${item.status === "Deceased" ? "!bg-neutral-400" : ""}`
                )
              )}
            </div>
          ) : (
            ""
          );
        }}
      />
      <Column
        dataField="percentage"
        allowSorting={false}
        allowEditing={false}
        width={90}
        alignment={"center"}
        headerCellRender={() => headerRenderer(`percentage`, "%")}
        cellRender={(cellData) => {
          const doc: LegalDocument = cellData.data;

          return doc?.beneficiaries?.length ? (
            <div className="flex h-full flex-col divide-y divide-neutral-400">
              {doc?.beneficiaries?.map((item) =>
                cellRenderer(
                  cellData,
                  `percentage-${item.externalCode}`,
                  item?.percent ? formatNumber(Number(item?.percent) / 100, "#.##%") : "",
                  "",
                  cn(
                    "h-full flex items-center min-h-[65.5px] justify-center px-2 !text-sm",
                    item.status === "Deceased" ? "!bg-neutral-400" : ""
                  )
                )
              )}
            </div>
          ) : (
            ""
          );
        }}
      />
    </DataGrid>
  );
}

const DocumentsWithDisclaimer = withDisclaimer(Documents);

export default DocumentsWithDisclaimer;
